import { visibleExceptProd } from './featureVisibility';

/**
 * Single switch for the nine NMR LipoProfile markers: LDL-P, HDL-P (Total),
 * Small LDL-P, LDL Size, Large VLDL-P, Large HDL-P, VLDL Size, HDL Size and the
 * LP-IR Score. They ship together, so they read one flag rather than each
 * repeating an env check that can drift. Same pattern as
 * liverCalculatedMarkersVisible(), myRangesVisible() and mvxVisible().
 *
 * Currently: visible in local dev and on slava, HIDDEN IN PROD. Closed to prod
 * on 2026-10-22, at the user's request, on the day the markers were built.
 *
 * ── Why it is held ─────────────────────────────────────────────────────────
 * The CODE is finished and tested. What is missing is the data:
 *
 *   1. No BloodworkRange rows exist yet. The proposed block is 145000 (step
 *      10) and the rows are drafted in ~/Downloads/nmr-lipoprofile-ranges.csv,
 *      but four of the nine still need upper bounds that Labcorp does not
 *      print, and nothing has been uploaded to either table. Until then every
 *      bar draws against no range, which renders empty rather than throwing
 *      (LeptinNoRange.test.ts is the general guard for that path).
 *   2. No About rows (FurtherConsiderations cat 700000+), so homeSectionFor()
 *      returns nothing and RangeCatalog files all nine under `skipped` —
 *      invisible on the My Ranges page.
 *   3. No root-cause bullets for categories 16120-16200. The rules fire today
 *      and render nothing, the way categories 703 and 420 already do.
 *
 * ── What this flag covers, and what it does not ────────────────────────────
 * Covered, because each one reads a blanked PanelMarker or this function:
 *   - the Lipid Results bar graphs (getLipidsPanelMarkers → hasValue())
 *   - the comparison table (fieldHasAnyValue → PanelMarker.hasValue())
 *   - the analysis PDF and the comparison PDF (same markers)
 *   - the AI raw-data export (walks Section.LIPIDS through the same getter)
 *   - the LipidsAnalysis root causes (a blank value is never above optimal)
 *   - the nine boxes on Add Test Results (bloodwork-entry.tsx filters on this)
 *   - the `labcorp-nmr-lipoprofile` prebuilt panel, which comes BACK when this
 *     is false so the existing My Markers route keeps working in prod
 *
 * NOT covered, deliberately:
 *   - `lambdas/uploadPdf/utils.js`, which cannot read this flag. It has its
 *     OWN gate instead, `HELD_FROM_PROD`, keyed on the Lambda function name:
 *     the nine aliases resolve on `uploadPdf-dev` only, and in prod the rows
 *     fall to My Markers as they always have, so the prebuilt panel keeps
 *     working. The lambda is therefore safe to promote while this is false.
 *     Keep the two in step: release both in the same change.
 *   - the stored values. Blanking happens on the PanelMarker, never on
 *     `Bloodwork`, so a test entered on slava keeps its numbers when prod loads
 *     it and keeps them when prod saves it again.
 *
 * ── Before releasing ───────────────────────────────────────────────────────
 * Return `true` here, or delete this module and drop its call sites. Then:
 *   - delete the nine ids from HELD_FROM_PROD in lambdas/uploadPdf/utils.js
 *     and promote the upload lambda (above);
 *   - announce the nine in settings-announcement.tsx, in whatever release
 *     actually carries them;
 *   - MIGRATION: a practitioner who already took the prebuilt panel in prod has
 *     these nine in their library as CUSTOM markers. Once the shipped ones
 *     appear, each pair is two of the same marker on one test, free to
 *     disagree — the exact trap `no panel marker shares a name with a marker
 *     LabSmarts ships` guards against. That needs a plan before release, not
 *     after.
 *
 * Full design and the corpus evidence: lipid-patterns-spec.md.
 */
export function nmrLipoProfileVisible(): boolean {
  return visibleExceptProd();
}
